/**
 * Calendrier de Paris (conception 5.3) : jours, semaines du lundi au dimanche,
 * jours ouvres et heures murales, calcules en heure de Paris quel que soit le
 * fuseau de la machine (les conteneurs tournent en UTC, les navigateurs la ou
 * sont leurs utilisateurs).
 *
 * Partage par l'API (bornes des requetes, quota de l'IA, taches planifiees) et
 * l'interface (suggestion d'une prochaine etape, report d'une relance) : un
 * seul calcul, teste une fois, changements d'heure compris.
 *
 * Seuls le samedi et le dimanche sont chomes : les jours feries ne sont pas
 * connus (hors perimetre).
 *
 * Aucune methode locale de `Date` (`getHours`, `setDate`...) ici : l'heure de
 * Paris se lit par `Intl`, le calendrier se compte en `Date.UTC`, deux
 * operations independantes du fuseau de la machine.
 */

export const FUSEAU_PARIS = 'Europe/Paris';

const MS_MINUTE = 60_000;

// 'en-CA' : des parties numeriques, quelle que soit la langue d'affichage.
const FORMAT_PARIS = new Intl.DateTimeFormat('en-CA', {
  timeZone: FUSEAU_PARIS, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

/** Date civile et heure murale a Paris (mois de 1 a 12). */
export interface HeureParis {
  annee: number;
  mois: number;
  jour: number;
  heure: number;
  minute: number;
  seconde: number;
}

/** Intervalle `[debut, fin[` : `fin` n'en fait pas partie. */
export interface Intervalle {
  debut: Date;
  fin: Date;
}

/** Heure murale de Paris a cet instant. */
export function partiesParis(instant: Date): HeureParis {
  const valeurs = new Map(
    FORMAT_PARIS.formatToParts(instant).map((partie): [string, number] => [partie.type, Number(partie.value)]),
  );
  const lire = (type: Intl.DateTimeFormatPartTypes): number => valeurs.get(type) ?? 0;
  return {
    annee: lire('year'),
    mois: lire('month'),
    jour: lire('day'),
    heure: lire('hour'),
    minute: lire('minute'),
    seconde: lire('second'),
  };
}

/** Avance de l'heure de Paris sur UTC a cet instant (1 h l'hiver, 2 h l'ete), en millisecondes. */
function decalageParis(instant: number): number {
  const p = partiesParis(new Date(instant));
  const mural = Date.UTC(p.annee, p.mois - 1, p.jour, p.heure, p.minute, p.seconde);
  // Arrondi a la minute : l'heure murale ignore les millisecondes de l'instant.
  return Math.round((mural - instant) / MS_MINUTE) * MS_MINUTE;
}

/**
 * Instant ou il est `heure:minute:seconde` a Paris le jour civil donne (mois de
 * 1 a 12). Les valeurs debordent comme avec `Date.UTC` -- le 32 decembre est le
 * 1er janvier --, ce qui sert au calcul des jours. Une heure sautee au
 * printemps (2 h 30 le dernier dimanche de mars) est reportee d'une heure.
 */
export function instantParis(annee: number, mois: number, jour: number, heure = 0, minute = 0, seconde = 0): Date {
  const mural = Date.UTC(annee, mois - 1, jour, heure, minute, seconde);
  // Premiere estimation avec l'avance lue a l'heure murale prise pour de
  // l'UTC, puis correction avec celle de l'instant estime : les deux ne
  // different qu'autour d'un changement d'heure.
  const estimation = mural - decalageParis(mural);
  return new Date(mural - decalageParis(estimation));
}

/** Minuit a Paris du jour civil donne (mois de 1 a 12). */
export function minuitParis(annee: number, mois: number, jour: number): Date {
  return instantParis(annee, mois, jour);
}

/** Minuit a Paris du jour civil ou tombe `reference` a Paris. */
export function debutJourParis(reference: Date = new Date()): Date {
  const p = partiesParis(reference);
  return minuitParis(p.annee, p.mois, p.jour);
}

/** Jour civil de Paris de `reference`, de minuit a minuit (23 ou 25 heures aux changements d'heure). */
export function bornesJourParis(reference: Date = new Date()): Intervalle {
  const p = partiesParis(reference);
  return { debut: minuitParis(p.annee, p.mois, p.jour), fin: minuitParis(p.annee, p.mois, p.jour + 1) };
}

/** Jour de la semaine d'une date civile : 1 = lundi ... 7 = dimanche. */
function jourSemaineCivil(annee: number, mois: number, jour: number): number {
  const jourUtc = new Date(Date.UTC(annee, mois - 1, jour)).getUTCDay();
  return jourUtc === 0 ? 7 : jourUtc;
}

/** Jour de la semaine a Paris : 1 = lundi ... 7 = dimanche. */
export function jourSemaineParis(reference: Date = new Date()): number {
  const p = partiesParis(reference);
  return jourSemaineCivil(p.annee, p.mois, p.jour);
}

/** Semaine civile de Paris contenant `reference` : du lundi 0 h au lundi suivant 0 h. */
export function bornesSemaineParis(reference: Date = new Date()): Intervalle {
  const p = partiesParis(reference);
  const lundi = p.jour - (jourSemaineCivil(p.annee, p.mois, p.jour) - 1);
  return { debut: minuitParis(p.annee, p.mois, lundi), fin: minuitParis(p.annee, p.mois, lundi + 7) };
}

function deuxChiffres(valeur: number): string {
  return String(valeur).padStart(2, '0');
}

/** Jour civil de Paris, `AAAA-MM-JJ`. */
export function jourParis(instant: Date): string {
  const p = partiesParis(instant);
  return `${p.annee}-${deuxChiffres(p.mois)}-${deuxChiffres(p.jour)}`;
}

/** Mois civil de Paris, `AAAA-MM`. */
export function moisParis(instant: Date = new Date()): string {
  const p = partiesParis(instant);
  return `${p.annee}-${deuxChiffres(p.mois)}`;
}

/** Bornes `[debut, fin[` d'un mois `AAAA-MM`, heure de Paris. */
export function bornesMoisParis(mois: string): Intervalle {
  const [annee = 0, numero = 1] = mois.split('-').map(Number);
  return { debut: minuitParis(annee, numero, 1), fin: minuitParis(annee, numero + 1, 1) };
}

/**
 * `jours` jours civils plus tard (plus tot si negatif), a la meme heure murale
 * de Paris, a la seconde pres : 10 h reste 10 h par-dessus un changement d'heure.
 */
export function ajouterJoursParis(reference: Date, jours: number): Date {
  const p = partiesParis(reference);
  return instantParis(p.annee, p.mois, p.jour + jours, p.heure, p.minute, p.seconde);
}

/**
 * `jours` jours ouvres (du lundi au vendredi) apres `depart`, a la meme heure
 * murale de Paris. Un depart le week-end compte a partir du lundi : samedi + 1
 * donne lundi. Jours feries ignores.
 */
export function ajouterJoursOuvres(depart: Date, jours: number): Date {
  if (!Number.isInteger(jours) || jours < 0) {
    throw new RangeError(`Nombre de jours ouvres invalide : ${jours}`);
  }
  const p = partiesParis(depart);
  let decalage = 0;
  let restants = jours;
  while (restants > 0) {
    decalage += 1;
    if (jourSemaineCivil(p.annee, p.mois, p.jour + decalage) <= 5) restants -= 1;
  }
  return instantParis(p.annee, p.mois, p.jour + decalage, p.heure, p.minute, p.seconde);
}

/** Le jour civil de Paris de `jour`, a `heure:minute` heure de Paris. */
export function aHeureParis(jour: Date, heure: number, minute = 0): Date {
  const valide = Number.isInteger(heure) && heure >= 0 && heure <= 23 && Number.isInteger(minute) && minute >= 0 && minute <= 59;
  if (!valide) throw new RangeError(`Heure invalide : ${heure}:${minute}`);
  const p = partiesParis(jour);
  return instantParis(p.annee, p.mois, p.jour, heure, minute);
}
